import { InputNumber, Modal, Select, Table } from "antd";
import { PI_DETAILS } from "apiServices/API_ENDPOINTS";
import { getData } from "apiServices/common";
import { alertPop } from "apiServices/common/helper";
import {
  getConversionData,
  getUnitList,
} from "components/Account/ImportPi/AddOrUpdatePi/AddPiForm";
import React, { useEffect, useState } from "react";
import { commaSeparateNumber, isArrayAndHasValue } from "utils/functions";

const { Option } = Select;

const PIDetailsModal = ({ isOpen, id, setPiModalDetails }) => {
  const [title, setTitle] = useState(null);
  const [isItemsLoading, setIsItemsLoading] = useState(false);
  const [data, setData] = useState([]);
  const [unitList, setUnitList] = useState([]);

  const onCloseModal = () => {
    setPiModalDetails({
      isOpen: false,
      id: null,
    });
  };

  const getTotalPriceOfItem = (value, data, idx, context) => {
    if (data) {
      data.forEach((item) => {
        if (item.id === idx) {
          item["rate"] = value;
          item[context] = (item.pi_qty || 0) * (value || 0);
        }
      });
    }

    setData([...data]);
  };

  const getTotalPriceOfItemByPiQty = (value, data, idx, context) => {
    if (data) {
      data.forEach((item) => {
        if (item.id === idx) {
          item["pi_qty"] = value;
          item[context] = (Number(value) || 0) * (Number(item.rate) || 0);
          item["balance_qty"] = Number(item.required_qty) - Number(value);
          getConversionData(item?.pi_unit_id, item, data, setData);
        }
      });
    }
  };

  const fetchData = async (id) => {
    setIsItemsLoading(true);
    try {
      let res = await getData(`${PI_DETAILS}/${id}`);

      if (res) {
        let masterData = res?.data?.data;
        
        setData(masterData?.import_pi_items || []);
        setTitle(masterData?.pi_number);
      }
    } catch (err) {
      alertPop("error", err);
    } finally {
      setIsItemsLoading(false);
    }
  };

  useEffect(() => {
    getUnitList(setUnitList);
  }, []);

  useEffect(() => {
    if (id) {
      fetchData(id);
    }
  }, [id]);

  const columns = [
    {
      title: "Item Category",
      dataIndex: ["category_info", "name"],
      key: "category_name",
      width: "20%",
      render: (text, record) => {
        return <span>{text}</span>;
      },
    },
    {
      title: "Item Name",
      dataIndex: ["item_info", "name"],
      key: "item_name",
      width: "20%",
      render: (text, record) => {
        return <span>{text}</span>;
      },
    },
    {
      title: "Style No",
      dataIndex: ["style_info", "style_no"],
      key: "style_no",
      width: "20%",
    },
    {
      title: "BOM",
      children: [
        {
          title: "Qty",
          dataIndex: "required_qty",
          key: "required_qty",
          render: (text, record, idx) => {
            return (
              <InputNumber
                style={{ width: "100px" }}
                disabled={true}
                value={Math.floor(record.required_qty)}
                edit={false}
                formatter={(value) => commaSeparateNumber(value)}
                size="small"
              />
            );
          },
        },
        {
          title: "Unit",
          dataIndex: ["unit_info", "name"],
          key: "unit_name",
          width: 180,
          render: (text) => <div style={{ width: "50px" }}>{text || ""}</div>,
        },
      ],
    },
    {
      title: "PI",
      children: [
        {
          title: "Qty",
          dataIndex: "pi_qty",
          key: "pi_qty",
          render: (text, record, idx) => {
            return (
              <InputNumber
                style={{ width: "100px" }}
                disabled={true}
                value={record.pi_qty}
                edit={true}
                onChange={(value) => {
                  getTotalPriceOfItemByPiQty(
                    value,
                    record,
                    record.id,
                    "amount",
                  );
                }}
                formatter={(value) => commaSeparateNumber(value)}
                size="small"
              />
            );
          },
        },
        {
          title: "Unit",
          dataIndex: "pi_unit_id",
          key: "pi_unit_id",
          render: (text, record, idx) => {
            return (
              <Select
                showSearch
                disabled={true}
                defaultValue={record?.pi_unit_id || record?.unit_id}
                value={record?.pi_unit_id || record?.unit_id}
                placeholder="Select Unit"
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
                onSelect={(value) =>
                  getConversionData(value, record, data, setData)
                }
                size="small"
                style={{ width: "100%" }}
              >
                {isArrayAndHasValue(unitList) &&
                  unitList.map((unit) => (
                    <Option key={unit.id} value={unit.id}>
                      {unit.name}
                    </Option>
                  ))}
              </Select>
            );
          },
        },
      ],
    },
    {
      title: "Qty in BOM Unit",
      dataIndex: "qty_in_bom_unit",
      key: "qty_in_bom_unit",
      render: (text, record, idx) => {
        return (
          <InputNumber
            style={{ width: "100px" }}
            disabled={true}
            value={
              !record?.pi_unit_id && !record?.qty_in_bom_unit
                ? record?.pi_qty
                : record?.qty_in_bom_unit
                ? Number(record.qty_in_bom_unit).toFixed(2)
                : 0
            }
            formatter={(value) => commaSeparateNumber(value)}
            size="small"
          />
        );
      },
    },
    {
      title: "Balance (Remaining)",
      dataIndex: "balance_qty",
      key: "balance_qty",
      render: (text, record, idx) => {
        return (
          <InputNumber
            style={{ width: "100px" }}
            disabled={true}
            value={Number(record.balance_qty).toFixed(2) || 0}
            formatter={(value) => commaSeparateNumber(value)}
            size="small"
          />
        );
      },
    },
    {
      title: "Rate",
      dataIndex: "rate",
      key: "rate",
      render: (text, record, idx) => {
        return (
          <InputNumber
            style={{ width: "100px" }}
            disabled={true} // This is already correct
            onChange={(value) => {
              getTotalPriceOfItem(value, record, record.id, "amount");
            }}
            value={record.rate || 0}
            size="small"
          />
        );
      },
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (text, record) => {
        return (
          <InputNumber
            style={{ width: "100px" }}
            disabled={true}
            value={Number(record.amount).toFixed(5) || 0}
            formatter={(value) => commaSeparateNumber(value)}
            size="small"
          />
        );
      },
    },
  ];

  const TableSummary = (pageData) => {
    let totalPiQty = 0;
    let totalAmount = 0;

    pageData?.pageData?.forEach((item) => {
      totalPiQty = totalPiQty + Number(item.pi_qty);
      totalAmount = totalAmount + Number(item.amount);
    });

    const totalData = {
      totalPiQty: totalPiQty ? totalPiQty.toFixed(2) : 0,
      totalAmount: totalAmount ? totalAmount.toFixed(2) : 0,
    };

    return (
      <Table.Summary fixed>
        <Table.Summary.Row style={{ backgroundColor: "#F5F5F5" }}>
          <Table.Summary.Cell index={0}>
            <strong>Total</strong>
          </Table.Summary.Cell>
          <Table.Summary.Cell colSpan={1} index={1}></Table.Summary.Cell>
          <Table.Summary.Cell index={2}></Table.Summary.Cell>
          <Table.Summary.Cell index={3}></Table.Summary.Cell>
          <Table.Summary.Cell index={4}></Table.Summary.Cell>
          <Table.Summary.Cell index={5}>
            <strong>
              {" "}
              {totalData?.totalPiQty
                ? commaSeparateNumber(totalData?.totalPiQty)
                : 0}
            </strong>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={6}></Table.Summary.Cell>
          <Table.Summary.Cell index={7}></Table.Summary.Cell>
          <Table.Summary.Cell index={8}></Table.Summary.Cell>
          <Table.Summary.Cell index={9}></Table.Summary.Cell>
          <Table.Summary.Cell index={10}>
            <strong>
              {totalData?.totalAmount
                ? commaSeparateNumber(totalData?.totalAmount)
                : 0}
            </strong>
          </Table.Summary.Cell>
        </Table.Summary.Row>
      </Table.Summary>
    );
  };
  return (
    <Modal
      open={isOpen}
      title={`PI Item Details for ${title}`}
      onCancel={() => {
        onCloseModal();
      }}
      maskStyle={{ backdropFilter: "blur(4px)" }}
      footer={null}
      width={1500}
    >
      <style>
        {`
          .pi-details-table .ant-table-container {
            position: relative;
          }
          .pi-details-table .ant-table-summary {
            position: sticky;
            bottom: 0;
            z-index: 2;
            background: #F5F5F5;
            box-shadow: 0 -2px 6px rgba(0,0,0,0.06);
          }
            .ant-table-sticky-scroll {
  display: none !important;
}
        `}
      </style>
      <Table
        className="pi-details-table"
        dataSource={data}
        columns={columns}
        summary={(pageData) => {
          return <TableSummary pageData={pageData} />;
        }}
        size="small"
        bordered
        loading={isItemsLoading}
        pagination={false}
        // scroll={{ x: true }}
        scroll={{ x: true, y: "60vh" }}
        sticky={{ getContainer: () => document.body, offsetHeader: 0 }}
      />
    </Modal>
  );
};

export default PIDetailsModal;
